import type { NotificationCategory, ReinforceOptionsRequest } from '@stars-and-ships/api-types';
import { useQueries, useQuery } from '@tanstack/react-query';
import { endpoints } from './endpoints';

export const queryKeys = {
  galaxies: ['galaxies'] as const,
  galaxy: (galaxyId: string) => ['galaxy', galaxyId] as const,
  galaxyMe: (galaxyId: string) => ['galaxy', galaxyId, 'me'] as const,
  map: (galaxyId: string) => ['galaxy', galaxyId, 'map'] as const,
  player: (galaxyId: string, playerId: string) => ['galaxy', galaxyId, 'player', playerId] as const,
  rankings: (galaxyId: string) => ['galaxy', galaxyId, 'rankings'] as const,
  systems: (galaxyId: string) => ['galaxy', galaxyId, 'system'] as const,
  system: (galaxyId: string, systemId: string) => ['galaxy', galaxyId, 'system', systemId] as const,
  /** Prefix of every inbox tab. */
  notifications: (galaxyId: string) => ['galaxy', galaxyId, 'notifications'] as const,
  notificationTab: (galaxyId: string, category: NotificationCategory) =>
    ['galaxy', galaxyId, 'notifications', category] as const,
  /** Prefix of every fleet query (own fleets, visible fleets). */
  fleets: (galaxyId: string) => ['galaxy', galaxyId, 'fleets'] as const,
  ownFleets: (galaxyId: string) => ['galaxy', galaxyId, 'fleets', 'own'] as const,
  visibleFleets: (galaxyId: string) => ['galaxy', galaxyId, 'fleets', 'visible'] as const,
  /** Timed orders live under the fleets prefix, so every fleet event refreshes them. */
  fleetOrders: (galaxyId: string) => ['galaxy', galaxyId, 'fleets', 'orders'] as const,
  reinforceOptions: (galaxyId: string, target: string, mission: string, arriveAt: string) =>
    ['galaxy', galaxyId, 'fleets', 'reinforce', target, mission, arriveAt] as const,
  route: (galaxyId: string, ...parts: (string | boolean)[]) =>
    ['galaxy', galaxyId, 'route', ...parts] as const,
  /** Prefix of every battle and report query. */
  battles: (galaxyId: string) => ['galaxy', galaxyId, 'battles'] as const,
  battleList: (galaxyId: string) => ['galaxy', galaxyId, 'battles', 'list'] as const,
  battle: (galaxyId: string, battleId: string) =>
    ['galaxy', galaxyId, 'battles', 'view', battleId] as const,
  reports: (galaxyId: string) => ['galaxy', galaxyId, 'battles', 'reports'] as const,
  report: (galaxyId: string, battleId: string) =>
    ['galaxy', galaxyId, 'battles', 'reports', battleId] as const,
  /** Prefix of scouts and intel reports. */
  intel: (galaxyId: string) => ['galaxy', galaxyId, 'intel'] as const,
  scouts: (galaxyId: string) => ['galaxy', galaxyId, 'intel', 'scouts'] as const,
  intelReports: (galaxyId: string, systemId?: string) =>
    ['galaxy', galaxyId, 'intel', 'reports', systemId ?? 'all'] as const,
  /** Prefix of claim lists and quotes. */
  claims: (galaxyId: string) => ['galaxy', galaxyId, 'claims'] as const,
  claimList: (galaxyId: string, scope: 'mine' | 'incoming') =>
    ['galaxy', galaxyId, 'claims', scope] as const,
  claimQuote: (galaxyId: string, systemId: string) =>
    ['galaxy', galaxyId, 'claims', 'quote', systemId] as const,
  codex: (galaxyId: string) => ['galaxy', galaxyId, 'codex'] as const,
  account: ['account'] as const,
  notificationSettings: ['account', 'notification-settings'] as const,
  /** Prefix of every alliance query. */
  alliances: (galaxyId: string) => ['galaxy', galaxyId, 'alliances'] as const,
  alliance: (galaxyId: string, allianceId: string) =>
    ['galaxy', galaxyId, 'alliances', 'view', allianceId] as const,
  allianceSearch: (galaxyId: string, search: string) =>
    ['galaxy', galaxyId, 'alliances', 'search', search] as const,
  allianceInvites: (galaxyId: string, allianceId: string) =>
    ['galaxy', galaxyId, 'alliances', 'invites', allianceId] as const,
  myAllianceInvites: (galaxyId: string) => ['galaxy', galaxyId, 'alliances', 'my-invites'] as const,
  allianceAttacks: (galaxyId: string, allianceId: string) =>
    ['galaxy', galaxyId, 'alliances', 'attacks', allianceId] as const,
  playerSearch: (galaxyId: string, search: string) =>
    ['galaxy', galaxyId, 'players', search] as const,
};

/** Fleet and battle lists also poll slowly, in case a live event is missed. */
const MILITARY_POLL_MS = 15_000;

export const useGalaxies = () =>
  useQuery({ queryKey: queryKeys.galaxies, queryFn: endpoints.galaxies });

/** Static balance numbers of one galaxy: they only change with a deploy. */
export const useCodex = (galaxyId: string) =>
  useQuery({
    queryKey: queryKeys.codex(galaxyId),
    queryFn: () => endpoints.codex(galaxyId),
    staleTime: Number.POSITIVE_INFINITY,
  });

export const useGalaxyMe = (galaxyId: string) =>
  useQuery({ queryKey: queryKeys.galaxyMe(galaxyId), queryFn: () => endpoints.galaxyMe(galaxyId) });

export const useMap = (galaxyId: string) =>
  useQuery({
    queryKey: queryKeys.map(galaxyId),
    queryFn: () => endpoints.map(galaxyId),
    staleTime: 60_000,
  });

export const useSystem = (galaxyId: string, systemId: string | undefined) =>
  useQuery({
    queryKey: queryKeys.system(galaxyId, systemId ?? ''),
    queryFn: () => endpoints.system(galaxyId, systemId as string),
    enabled: Boolean(systemId),
  });

/** Several systems at once (Empire screen), sharing the cache and refreshes of `useSystem`. */
export const useSystems = (galaxyId: string, systemIds: string[]) =>
  useQueries({
    queries: systemIds.map((systemId) => ({
      queryKey: queryKeys.system(galaxyId, systemId),
      queryFn: () => endpoints.system(galaxyId, systemId),
    })),
  });

export const useNotifications = (galaxyId: string, category: NotificationCategory) =>
  useQuery({
    queryKey: queryKeys.notificationTab(galaxyId, category),
    queryFn: () => endpoints.notifications(galaxyId, category),
  });

export const useNotificationSettings = () =>
  useQuery({ queryKey: queryKeys.notificationSettings, queryFn: endpoints.notificationSettings });

export const useFleets = (galaxyId: string) =>
  useQuery({
    queryKey: queryKeys.ownFleets(galaxyId),
    queryFn: () => endpoints.fleets(galaxyId),
    refetchInterval: MILITARY_POLL_MS,
  });

export const useVisibleFleets = (galaxyId: string) =>
  useQuery({
    queryKey: queryKeys.visibleFleets(galaxyId),
    queryFn: () => endpoints.visibleFleets(galaxyId),
    refetchInterval: MILITARY_POLL_MS,
  });

export const useFleetOrders = (galaxyId: string) =>
  useQuery({
    queryKey: queryKeys.fleetOrders(galaxyId),
    queryFn: () => endpoints.fleetOrders(galaxyId),
    refetchInterval: MILITARY_POLL_MS,
  });

export const useReinforceOptions = (galaxyId: string, request: ReinforceOptionsRequest | null) =>
  useQuery({
    queryKey: queryKeys.reinforceOptions(
      galaxyId,
      request?.targetSystemId ?? '',
      request?.mission ?? '',
      request?.arriveAt ?? ''
    ),
    queryFn: () => endpoints.reinforceOptions(galaxyId, request as ReinforceOptionsRequest),
    enabled: request !== null,
  });

export const useAllianceAttacks = (galaxyId: string, allianceId: string | undefined) =>
  useQuery({
    queryKey: queryKeys.allianceAttacks(galaxyId, allianceId ?? ''),
    queryFn: () => endpoints.allianceAttacks(galaxyId, allianceId as string),
    enabled: Boolean(allianceId),
    refetchInterval: MILITARY_POLL_MS,
  });

export const useBattles = (galaxyId: string) =>
  useQuery({
    queryKey: queryKeys.battleList(galaxyId),
    queryFn: () => endpoints.battles(galaxyId),
    refetchInterval: MILITARY_POLL_MS,
  });

export const useBattle = (galaxyId: string, battleId: string) =>
  useQuery({
    queryKey: queryKeys.battle(galaxyId, battleId),
    queryFn: () => endpoints.battle(galaxyId, battleId),
    enabled: Boolean(battleId),
  });

export const useBattleReports = (galaxyId: string) =>
  useQuery({
    queryKey: queryKeys.reports(galaxyId),
    queryFn: () => endpoints.battleReports(galaxyId),
  });

export const useScouts = (galaxyId: string) =>
  useQuery({
    queryKey: queryKeys.scouts(galaxyId),
    queryFn: () => endpoints.scouts(galaxyId),
    refetchInterval: MILITARY_POLL_MS,
  });

export const useIntelReports = (galaxyId: string, systemId?: string) =>
  useQuery({
    queryKey: queryKeys.intelReports(galaxyId, systemId),
    queryFn: () => endpoints.intelReports(galaxyId, systemId),
  });

export const useClaims = (galaxyId: string, scope: 'mine' | 'incoming') =>
  useQuery({
    queryKey: queryKeys.claimList(galaxyId, scope),
    queryFn: () => endpoints.claims(galaxyId, scope),
    refetchInterval: MILITARY_POLL_MS,
  });

/** The quote changes whenever the player's owned systems or open claims change. */
export const useClaimQuote = (galaxyId: string, systemId: string, enabled: boolean) =>
  useQuery({
    queryKey: queryKeys.claimQuote(galaxyId, systemId),
    queryFn: () => endpoints.claimQuote(galaxyId, systemId),
    enabled,
    retry: false,
  });

export const useBattleReport = (galaxyId: string, battleId: string) =>
  useQuery({
    queryKey: queryKeys.report(galaxyId, battleId),
    queryFn: () => endpoints.battleReport(galaxyId, battleId),
    enabled: Boolean(battleId),
  });

/** The account (fresh `phoneVerified` after an operator verified the phone). */
export const useAccount = () => useQuery({ queryKey: queryKeys.account, queryFn: endpoints.me });

export const useAlliance = (galaxyId: string, allianceId: string | undefined) =>
  useQuery({
    queryKey: queryKeys.alliance(galaxyId, allianceId ?? ''),
    queryFn: () => endpoints.alliance(galaxyId, allianceId as string),
    enabled: Boolean(allianceId),
  });

export const usePublicPlayer = (galaxyId: string, playerId: string | undefined) =>
  useQuery({
    queryKey: queryKeys.player(galaxyId, playerId ?? ''),
    queryFn: () => endpoints.publicPlayer(galaxyId, playerId as string),
    enabled: Boolean(playerId),
  });

export const useRankings = (galaxyId: string) =>
  useQuery({
    queryKey: queryKeys.rankings(galaxyId),
    queryFn: () => endpoints.rankings(galaxyId),
  });

export const useAllianceSearch = (galaxyId: string, search: string, enabled: boolean) =>
  useQuery({
    queryKey: queryKeys.allianceSearch(galaxyId, search),
    queryFn: () => endpoints.alliances(galaxyId, search),
    enabled,
  });

export const useAllianceInvites = (galaxyId: string, allianceId: string, enabled: boolean) =>
  useQuery({
    queryKey: queryKeys.allianceInvites(galaxyId, allianceId),
    queryFn: () => endpoints.allianceInvites(galaxyId, allianceId),
    enabled,
  });

export const useMyAllianceInvites = (galaxyId: string, enabled: boolean) =>
  useQuery({
    queryKey: queryKeys.myAllianceInvites(galaxyId),
    queryFn: () => endpoints.myAllianceInvites(galaxyId),
    enabled,
  });

export const usePlayerSearch = (galaxyId: string, search: string) =>
  useQuery({
    queryKey: queryKeys.playerSearch(galaxyId, search),
    queryFn: () => endpoints.searchPlayers(galaxyId, search),
    enabled: search.trim().length > 0,
  });
